// Issue quick actions — the STATUS / PRIORITY / ASSIGNED-TO controls
// shared by the resolve page (as a bar above the primary pane) and the
// coordination issues list (one control per table cell).
//
//   <IssueActionBar issue={issue} onChanged={reload} />
//   <IssueStatusSelect issue onChanged={(patch) => …} />
//   <IssuePrioritySelect issue onChanged={(patch) => …} />
//   <IssueAssignSelect issue onChanged={(patch) => …} compact />
//   <IssueDueDateSelect issue onChanged={(patch) => …} />
//
// onChanged receives a partial patch ({status} / {priority} / {assign:
// true}) after the API call succeeded — callers either merge it into
// local state or refetch. Rosters are fetched once per project and
// cached module-wide so a 50-row table doesn't fan out 50 requests.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Input, InputNumber, Modal, Select, Radio, message, Popconfirm, Tag } from "antd";
import { PlusOutlined, UserAddOutlined } from "@ant-design/icons";
import { url, tokenRef } from "api";
import { updateIssueStatus, updateIssuePriority, updateIssueDueDate, updateIssueProgress, getGuestToken } from "api/issue/issueApi"
import { T } from "pages/Issue/theme";
import { peoplePickerOptions, renderPeopleOption } from "components/PeoplePicker/PeoplePicker";
import { groupLabel } from "components/PeoplePicker/peoplePickerOptions";
import { dueInputValue, isPastDue, daysToDue } from "helpers/issueDueDate";
import usePermissions from "hooks/usePermissions";
import { ALL_PERMISSIONS } from "constants/permissionsConstants";

// Signed-in users need issueEdit (partner PMs / supervisors and partner
// employees both hold it; the server enforces it on PUT /issue/:id,
// /status, /priority, /assign, /responsibles). Guest sessions carry
// their own per-issue permission and are checked server-side, so the
// controls stay enabled when there is no signed-in user.
function useIssueEditAllowed() {
  const { has, userInfo } = usePermissions();
  if (!userInfo?.id && !userInfo?._id) return true;
  return has(ALL_PERMISSIONS.ISSUE_EDIT);
}
const NO_EDIT_TITLE = "You do not have the issueEdit permission";

// The statuses anyone with issueEdit may set from a dropdown.
//
// `resolved` and `abandoned` were missing, so the control could move an
// issue between the three live states and no further - the two endings
// the server has always accepted on PUT /issue/:id/status were the two
// nobody could pick. A settled issue showed its own status back as a
// disabled option, which read as "this is fixed" rather than "you
// cannot set this".
//
// EVERY ENDING IS OFFERED TO EVERYONE HOLDING issueEdit, BECAUSE THE
// SERVER DECIDES WHAT IT MEANS. Owner-side (project owner, owner
// company, or anyone on the project roster) ends the issue directly;
// a partner or a link guest files the request in the owner's Review
// Center and the issue sits at `under_review` until it is approved.
// The caller reports whichever happened - see `pendingReview` below.
//
// "In review" is NOT on this list. It is a state the review gate owns:
// an issue arrives there because somebody outside the job claimed an
// ending, and it leaves when a reviewer answers. Offering it as an
// ordinary pick let anyone park an issue in a queue that only lists
// submissions, so there was nothing to approve and nothing to send back
// - the issue simply stopped, for everyone. (Prod SPX-314, 2026-08-28.)
// It is still shown, disabled, when an issue is already there.
export const STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "pending", label: "Pending" },
  { value: "resolved", label: "Resolved" },
];
// Abandoning is calling the work OFF - a decision about scope rather
// than a report on progress. It used to be hidden from partner
// companies entirely, which said the wrong thing twice: a subcontractor
// who knows a job is not happening had no way to say so, and the API
// accepted it from them anyway and applied it on the spot. It is on
// offer now, and the server routes it through review like any other
// ending a partner claims.
export const ABANDON_OPTION = { value: "abandoned", label: "Abandoned" };

// The options to show, given where the issue is now. An unknown or
// terminal current value (e.g. `abandoned`) is appended disabled, so the
// control always displays the truth.
//
// `ownerSide` is still accepted so callers read the same, but it no
// longer decides what is on the list - only the server does.
export function statusOptionsFor(current, ownerSide) {
  const base = [...STATUS_OPTIONS, ABANDON_OPTION];
  if (current && !base.some((o) => o.value === current)) {
    base.push({ value: current, label: String(current).replace(/_/g, " "), disabled: true });
  }
  return base;
}

// What the server did with an ending, in one line. It answers a
// `message` written for the ending that was actually requested
// (utils/issueReviewGate); these are the fallbacks.
export function statusResultMessage(r, wanted) {
  if (r && r.pendingReview) {
    return r.message
      || `Sent to the project owner's Review Center - it takes effect once approved`;
  }
  return wanted === "abandoned" ? "Issue abandoned" : "Status updated";
}
export const PRIORITY_OPTIONS = [
  { value: "urgent", label: "Urgent" },
  { value: "normal", label: "Normal" },
  { value: "low", label: "Low" },
];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ---- roster cache (projectId → Promise<groups>) -------------------------
const rosterCache = new Map();
function fetchRoster(projectId) {
  if (!projectId) return Promise.resolve([]);
  if (!rosterCache.has(projectId)) {
    const p = fetch(`${url}/projects/${projectId}/participants/roster`, {
      headers: { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` },
    })
      .then(async (r) => { const b = await r.json(); return r.ok && Array.isArray(b) ? b : []; })
      .catch(() => []);
    rosterCache.set(projectId, p);
    // Let a later mount refresh after 60s (people get added).
    setTimeout(() => rosterCache.delete(projectId), 60 * 1000);
  }
  return rosterCache.get(projectId);
}
export function invalidateRoster(projectId) { rosterCache.delete(projectId); }

export function useProjectRoster(projectId) {
  const [groups, setGroups] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!projectId) { setGroups([]); return undefined; }
    fetchRoster(projectId).then((g) => { if (!cancelled) setGroups(g); });
    return () => { cancelled = true; };
  }, [projectId]);
  return groups;
}

const idOf = (v) => (v && typeof v === "object" ? String(v._id) : (v ? String(v) : ""));
const personLabel = (u) => {
  if (!u || typeof u !== "object") return undefined;
  const nm = [u.name, u.lastName].filter(Boolean).join(" ") || u.email;
  return u.email ? `${nm} — ${u.email}` : nm;
};

// ---- STATUS ----------------------------------------------------------------
export function IssueStatusSelect({ issue, onChanged, style, size = "small" }) {
  const [saving, setSaving] = useState(false);
  const allowed = useIssueEditAllowed();
  const value = issue?.status;
  // Every ending is on the list for everyone; the server decides
  // whether picking one ends the issue or asks the owner to.
  const options = statusOptionsFor(value);
  const change = async (v) => {
    setSaving(true);
    try {
      const r = await updateIssueStatus(issue._id, v);
      message.success(statusResultMessage(r, v));
      await onChanged?.({ status: r && r.pendingReview ? "under_review" : v });
    } catch (e) {
      message.error(e.message || "Failed to update status");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Select size={size} value={value} options={options} onChange={change}
      loading={saving} disabled={saving || !allowed} title={allowed ? undefined : NO_EDIT_TITLE}
      style={{ width: 130, ...style }}
      onClick={(e) => e.stopPropagation()} />
  );
}

// ---- PRIORITY --------------------------------------------------------------
export function IssuePrioritySelect({ issue, onChanged, style, size = "small" }) {
  const [saving, setSaving] = useState(false);
  const allowed = useIssueEditAllowed();
  const change = async (v) => {
    setSaving(true);
    try {
      await updateIssuePriority(issue._id, v);
      message.success("Priority updated");
      await onChanged?.({ priority: v });
    } catch (e) {
      message.error(e.message || "Failed to update priority");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Select size={size} value={issue?.priority || "normal"} options={PRIORITY_OPTIONS}
      onChange={change} loading={saving} disabled={saving || !allowed} title={allowed ? undefined : NO_EDIT_TITLE}
      style={{ width: 110, ...style }}
      onClick={(e) => e.stopPropagation()} />
  );
}

// ---- DUE DATE --------------------------------------------------------------
// One <input type="date"> per row: whoever can edit the issue picks the
// day it is due, and clearing the box removes it. Native rather than
// antd's DatePicker on purpose — the batch entry page already uses the
// same control, and it round-trips the plain "YYYY-MM-DD" the API takes
// with no date-library adapter in the middle. The box turns red once the
// due day has passed and amber on the last day, so an overdue row is
// visible without opening it.
export function IssueDueDateSelect({ issue, onChanged, style, title }) {
  const allowed = useIssueEditAllowed();
  const [saving, setSaving] = useState(false);
  const value = dueInputValue(issue?.dueDate);
  // A resolved / abandoned issue is not "late" — the work is off
  // the table, so its date stays neutral instead of shouting red down a
  // list of finished rows.
  const settled = ["resolved", "abandoned"].includes(issue?.status);
  const late = !settled && isPastDue(issue?.dueDate);
  const days = daysToDue(issue?.dueDate);
  const soon = !settled && !late && days !== null && days <= 1;

  const change = async (next) => {
    if (next === value) return;
    setSaving(true);
    try {
      const r = await updateIssueDueDate(issue._id, next);
      message.success(next ? "Due date updated" : "Due date cleared");
      await onChanged?.({ dueDate: r?.dueDate ?? null, plannedEnd: r?.plannedEnd ?? null });
    } catch (e) {
      message.error(e.message || "Failed to update the due date");
    } finally {
      setSaving(false);
    }
  };

  return (
    <input
      type="date"
      value={value}
      disabled={saving || !allowed}
      title={allowed ? (title || (value ? "Due date — clear the box to remove it" : "Set a due date")) : NO_EDIT_TITLE}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => change(e.target.value)}
      style={{
        border: `1px solid ${late ? T.removed : soon ? T.amber : T.line2}`,
        background: saving ? T.bg : late ? T.removedBg : soon ? T.ambBg : "#fff",
        color: late ? T.removed2 : T.text,
        borderRadius: 4,
        padding: "2px 6px",
        fontSize: 12,
        width: 132,
        cursor: allowed ? "pointer" : "not-allowed",
        ...style,
      }}
    />
  );
}

// ---- ASSIGNED TO -----------------------------------------------------------
// Searchable by name or email; declared partner contacts without an
// account are assignable by email (account auto-provisioned + magic
// link); a typed unknown email is offered directly; "Add person" takes
// name + email → PUT /issue/:id/assign {email, name}.
export function IssueAssignSelect({ issue, onChanged, style, size = "small", compact = false }) {
  const projectId = issue?.projectId?._id || issue?.projectId;
  const rosterGroups = useProjectRoster(projectId);
  const [assigning, setAssigning] = useState(false);
  const allowed = useIssueEditAllowed();
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState("");
  const [addEmail, setAddEmail] = useState("");

  const options = useMemo(() => {
    // The shared picker's options (components/PeoplePicker, owner
    // 2026-10-15): partners under their trade, people tagged by kind. A
    // partner executive is greyed out - a reader, never the assignee.
    const groups = peoplePickerOptions(rosterGroups, {
      personValue: (p) => (p.userId ? String(p.userId) : `email:${String(p.email || "").toLowerCase()}`),
      executives: "disable",
    });
    const typed = search.trim().toLowerCase();
    if (EMAIL_RE.test(typed) && !groups.some((g) => g.options.some((o) => o.search.includes(typed)))) {
      groups.push({ label: "New person", options: [{ value: `email:${typed}`, label: `Assign & email → ${typed}`, search: typed }] });
    }
    return groups;
  }, [rosterGroups, search]);

  const at = issue?.assignedTo?.userId;
  const ru0 = (issue?.responsibleUsers || [])[0];
  // responsibleUsers rows are {userId} on the resolve page but flat user
  // objects on the list page — accept both.
  const ruUser = ru0 && ru0.userId !== undefined ? ru0.userId : ru0;
  const currentId = idOf(at) || idOf(ruUser) || undefined;
  const currentLabel = personLabel(at && typeof at === "object" ? at : ruUser);
  const value = currentId
    ? (options.some((g) => g.options.some((o) => o.value === currentId)) || !currentLabel
        ? currentId
        : { value: currentId, label: currentLabel })
    : undefined;

  const assignTo = async (payload) => {
    setAssigning(true);
    try {
      const resp = await fetch(`${url}/issue/${issue._id}/assign`, {
        method: "PUT",
        // A partner on an emailed link has no user token - their session
        // is the x-guest-token the server's authenticateGuestOrUser
        // reads. Sending both means one call works for either.
        headers: {
          "Content-Type": "application/json",
          ...(localStorage.getItem(tokenRef) ? { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` } : {}),
          ...(getGuestToken() ? { "x-guest-token": getGuestToken() } : {}),
        },
        body: JSON.stringify(payload),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) { message.error(body.err || "Failed to assign issue"); return false; }
      message.success(body.account?.email
        ? `Assigned to ${body.account.name || body.account.email} — link emailed to ${body.account.email}`
        : "Assignee updated — they've been emailed");
      setSearch("");
      invalidateRoster(projectId);
      await onChanged?.({ assign: true, assignedTo: body.assignedTo, account: body.account });
      return true;
    } catch (e) {
      message.error(e.message || "Failed to assign issue");
      return false;
    } finally {
      setAssigning(false);
    }
  };
  const handleChange = (v) => (String(v).startsWith("email:")
    ? assignTo({ email: String(v).slice(6) })
    : assignTo({ userId: v }));
  const submitAdd = async () => {
    const email = addEmail.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) { message.warning("Enter a valid email"); return; }
    if (!addName.trim()) { message.warning("Enter their name"); return; }
    const ok = await assignTo({ email, name: addName.trim() });
    if (ok) { setAddOpen(false); setAddName(""); setAddEmail(""); }
  };

  return (
    <span onClick={(e) => e.stopPropagation()}>
      <Select
        size={size}
        showSearch
        value={value}
        placeholder={rosterGroups === null ? "Loading…" : (compact ? "Assign…" : "Search name or email…")}
        options={options}
        onChange={handleChange}
        onSearch={setSearch}
        searchValue={search}
        loading={assigning || rosterGroups === null}
        disabled={assigning || !allowed} title={allowed ? undefined : NO_EDIT_TITLE}
        style={{ minWidth: compact ? 190 : 260, width: compact ? 220 : undefined, flex: compact ? undefined : "0 1 340px", ...style }}
        dropdownMatchSelectWidth={false}
        optionRender={(o) => (o.data && o.data.person ? renderPeopleOption(o) : o.label)}
        filterOption={(input, option) =>
          String(option?.search || option?.label || "").toLowerCase().includes(input.toLowerCase())}
        notFoundContent={search.trim()
          ? <span style={{ fontSize: 12, color: T.muted }}>No match — type a full email to assign it, or use Add person</span>
          : null}
        dropdownRender={(menu) => (
          <div>
            {menu}
            <div style={{ borderTop: `1px solid ${T.line}`, padding: "6px 8px" }}>
              <Button size="small" type="link" icon={<PlusOutlined />} style={{ padding: 0 }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setAddOpen(true)}>
                Add person (name + email)
              </Button>
            </div>
          </div>
        )}
      />
      <Modal
        open={addOpen}
        onCancel={() => setAddOpen(false)}
        onOk={submitAdd}
        okText="Assign & email"
        confirmLoading={assigning}
        width={380}
        title="Assign to a new person"
        destroyOnClose
      >
        <div style={{ fontSize: 12.5, color: T.muted, marginBottom: 10 }}>
          They don't need a BidLight account — the issue is reassigned to them and they get a personal link that opens it signed in.
        </div>
        <Input placeholder="Full name" value={addName} onChange={(e) => setAddName(e.target.value)} style={{ marginBottom: 8 }} autoFocus />
        <Input placeholder="Email" type="email" value={addEmail} onChange={(e) => setAddEmail(e.target.value)} onPressEnter={submitAdd} />
      </Modal>
    </span>
  );
}

// ---- ADD PARTNER (extra responsible) --------------------------------------
// "Add partner" button → dropdown of the project roster (search name or
// email) to ADD someone as responsible without replacing the assignee,
// plus "Add new contact…": step 1 first name + email → Send → step 2
// pick which contractor (project participant) they work for → Complete.
// Backend: POST /issue/:id/responsibles.
// ---- PROGRESS TO COMPLETION ----------------------------------------------
// How far the work has got, as the person doing it reports it - a
// different question from status, and the number that rolls up onto the
// schedule activity this issue belongs to. Editable by whoever can edit
// the issue, which includes a partner on their emailed link.
export function IssueProgressControl({ issue, onChanged, size = "small" }) {
  const allowed = useIssueEditAllowed();
  const [value, setValue] = useState(typeof issue?.progress === "number" ? issue.progress : 0);
  const [busy, setBusy] = useState(false);
  const lastSaved = useRef(typeof issue?.progress === "number" ? issue.progress : 0);

  useEffect(() => {
    const v = typeof issue?.progress === "number" ? issue.progress : 0;
    setValue(v);
    lastSaved.current = v;
  }, [issue?._id, issue?.progress]);

  const commit = async (next) => {
    const v = Math.max(0, Math.min(100, Math.round(Number(next))));
    if (!isFinite(v) || v === lastSaved.current) return;
    setBusy(true);
    try {
      const r = await updateIssueProgress(issue._id, v);
      lastSaved.current = v;
      const act = r && r.activity;
      message.success(
        act && act.count
          ? `Progress ${v}% — the activity now reports ${act.progress}% across ${act.count} issue${act.count === 1 ? "" : "s"}`
          : `Progress ${v}%`
      );
      await onChanged?.({ progress: true });
    } catch (e) {
      setValue(lastSaved.current);
      message.error(e?.err || e?.message || "Could not update the progress");
    } finally {
      setBusy(false);
    }
  };

  const done = value >= 100;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }} onClick={(e) => e.stopPropagation()}>
      <InputNumber
        size={size}
        min={0}
        max={100}
        step={5}
        value={value}
        disabled={!allowed || busy}
        title={allowed ? "Progress to completion" : NO_EDIT_TITLE}
        onChange={(v) => setValue(v === null ? 0 : v)}
        onBlur={() => commit(value)}
        onPressEnter={() => commit(value)}
        formatter={(v) => `${v}%`}
        parser={(v) => String(v).replace("%", "")}
        style={{ width: 82 }}
      />
      <span
        title={`${value}% reported`}
        style={{
          width: 54, height: 6, borderRadius: 3, background: T.line2,
          overflow: "hidden", display: "inline-block",
        }}
      >
        <span style={{
          display: "block", height: "100%", width: `${Math.max(0, Math.min(100, value))}%`,
          background: done ? T.added2 : T.navy,
        }} />
      </span>
    </span>
  );
}

export function IssueAddPartner({ issue, onChanged, size = "small" }) {
  const allowed = useIssueEditAllowed();
  const projectId = issue?.projectId?._id || issue?.projectId;
  const rosterGroups = useProjectRoster(projectId);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [step, setStep] = useState(0); // 0 closed, 1 name+email, 2 pick contractor
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [companyId, setCompanyId] = useState("");


  // Everyone currently on the issue, as the select's value: responsible
  // users, the assignee, and no-account contacts. Ticking a roster entry
  // adds a person (they start getting the issue's updates); unticking -
  // or the x on their tag - takes them off (DELETE /responsibles/:who).
  const nameOfUser = (u) => (u && typeof u === "object") ? ([u.name, u.lastName].filter(Boolean).join(" ") || u.email || "") : "";
  const current = useMemo(() => {
    const rows = [];
    const seen = new Set();
    for (const r of issue?.responsibleUsers || []) {
      const u = r?.userId !== undefined ? r.userId : r;
      const id = idOf(u);
      if (!id || seen.has(id)) continue;
      seen.add(id);
      rows.push({ value: id, label: nameOfUser(u) || "Responsible" });
    }
    const a = issue?.assignedTo?.userId;
    if (a && idOf(a) && !seen.has(idOf(a))) rows.push({ value: idOf(a), label: `${nameOfUser(a) || "Assignee"} (assignee)` });
    for (const c of issue?.responsibleContacts || []) {
      if (c?.email) rows.push({ value: `contact:${String(c.email).toLowerCase()}`, label: `${c.name || c.email} (link)` });
    }
    return rows;
  }, [issue?.responsibleUsers, issue?.assignedTo, issue?.responsibleContacts]);
  const options = useMemo(() => {
    const inRoster = new Set();
    // Same groups as the create page: the shared picker's (components/
    // PeoplePicker) - company - trades, people tagged by kind.
    const groups = peoplePickerOptions(rosterGroups, {
      personValue: (p, g) => {
        if (p.userId) { inRoster.add(String(p.userId)); return String(p.userId); }
        return `email:${String(p.email || "").toLowerCase()}|${g.companyId}`;
      },
      executives: "disable",
    });
    // People on the issue who are not on the roster (a contact, an
    // assignee from elsewhere) still need an option, or their tag has
    // no name.
    const extra = current.filter((c) => !inRoster.has(c.value)).map((c) => ({ ...c, search: c.label.toLowerCase() }));
    return extra.length ? [...groups, { label: "On this issue", options: extra }] : groups;
  }, [rosterGroups, current]);

  const post = async (payload) => {
    setBusy(true);
    try {
      const resp = await fetch(`${url}/issue/${issue._id}/responsibles`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem(tokenRef)}` },
        body: JSON.stringify(payload),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) { message.error(body.err || "Could not add partner"); return false; }
      message.success(body.already
        ? `${body.account?.name || body.account?.email} was already responsible — reminder emailed`
        : `${body.account?.name || body.account?.email} added — link emailed to ${body.account?.email}`);
      invalidateRoster(projectId);
      await onChanged?.({ responsibles: true, responsibleUsers: body.responsibleUsers });
      return true;
    } catch (e) {
      message.error(e.message || "Could not add partner");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const pick = async (v) => {
    if (String(v).startsWith("email:")) {
      const [addr, cid] = String(v).slice(6).split("|");
      await post({ email: addr, companyId: cid || undefined });
    } else {
      await post({ userId: v });
    }
    setSearch("");
  };
  const unpick = async (v) => {
    const who = String(v).startsWith("contact:") ? `contact:${encodeURIComponent(String(v).slice(8))}` : String(v);
    setBusy(true);
    try {
      const resp = await fetch(`${url}/issue/${issue._id}/responsibles/${who}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` },
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) { message.error(body.err || "Could not remove"); return; }
      message.success(`${body.removed || "Removed"} - they will no longer be notified about this issue`);
      await onChanged?.({ responsibles: true, responsibleUsers: body.responsibleUsers, responsibleContacts: body.responsibleContacts, assignedTo: body.assignedTo });
    } catch (e) {
      message.error(e.message || "Could not remove");
    } finally {
      setBusy(false);
    }
  };

  const contractors = (rosterGroups || []).map((g) => ({
    companyId: g.companyId, name: groupLabel(g) || "Company", role: g.role,
  }));

  const sendStep1 = () => {
    if (!firstName.trim()) { message.warning("Enter their first name"); return; }
    if (!EMAIL_RE.test(email.trim().toLowerCase())) { message.warning("Enter a valid email"); return; }
    // Pre-select the contractor if the email matches a roster contact.
    const hit = (rosterGroups || []).find((g) => (g.users || []).some((u) => String(u.email || "").toLowerCase() === email.trim().toLowerCase()));
    setCompanyId(hit ? hit.companyId : (contractors.find((c) => c.role !== "owner")?.companyId || ""));
    setStep(2);
  };
  const complete = async () => {
    if (!companyId) { message.warning("Pick the contractor this person works for"); return; }
    const ok = await post({ email: email.trim().toLowerCase(), name: firstName.trim(), companyId });
    if (ok) { setStep(0); setFirstName(""); setEmail(""); setCompanyId(""); }
  };

  return (
    <span onClick={(e) => e.stopPropagation()} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <Select
        size={size}
        mode="multiple"
        showSearch
        open={open}
        onDropdownVisibleChange={setOpen}
        value={current.map((c) => c.value)}
        placeholder={<span><UserAddOutlined /> Select users...</span>}
        options={options}
        onSelect={pick}
        onDeselect={unpick}
        onSearch={setSearch}
        searchValue={search}
        maxTagCount="responsive"
        loading={busy || rosterGroups === null}
        disabled={busy || !allowed} title={allowed ? undefined : NO_EDIT_TITLE}
        style={{ minWidth: 220, maxWidth: 420 }}
        dropdownMatchSelectWidth={340}
        optionRender={(o) => (o.data && o.data.person ? renderPeopleOption(o) : o.label)}
        filterOption={(input, option) =>
          String(option?.search || option?.label || "").toLowerCase().includes(input.toLowerCase())}
        notFoundContent={<span style={{ fontSize: 12, color: T.muted }}>No match — add a new contact below</span>}
        dropdownRender={(menu) => (
          <div>
            {menu}
            <div style={{ borderTop: `1px solid ${T.line}`, padding: "6px 8px" }}>
              <Button size="small" type="link" icon={<PlusOutlined />} style={{ padding: 0 }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { setOpen(false); setStep(1); }}>
                Add new contact…
              </Button>
            </div>
          </div>
        )}
      />

      <Modal
        open={step === 1}
        title="Add new contact"
        onCancel={() => setStep(0)}
        onOk={sendStep1}
        okText="Send"
        width={380}
        destroyOnClose
      >
        <div style={{ fontSize: 12.5, color: T.muted, marginBottom: 10 }}>
          They'll be added as responsible for this issue and emailed a personal link that opens it — no BidLight account needed.
        </div>
        <Input placeholder="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} style={{ marginBottom: 8 }} autoFocus />
        <Input placeholder="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} onPressEnter={sendStep1} />
      </Modal>

      <Modal
        open={step === 2}
        title={`Which contractor does ${firstName || "this person"} work for?`}
        onCancel={() => setStep(0)}
        onOk={complete}
        okText="Complete"
        confirmLoading={busy}
        width={420}
        destroyOnClose
      >
        <div style={{ fontSize: 12.5, color: T.muted, marginBottom: 10 }}>
          Contractors on this project — <b>{email}</b> will be registered under the one you pick.
        </div>
        <Radio.Group value={companyId} onChange={(e) => setCompanyId(e.target.value)} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {contractors.map((c) => (
            <Radio key={c.companyId} value={c.companyId}>
              {c.name}{c.role === "owner" ? " (project owner)" : c.role ? ` · ${c.role}` : ""}
            </Radio>
          ))}
        </Radio.Group>
        {contractors.length === 0 && <div style={{ color: T.muted, fontSize: 12 }}>No contractors on this project yet — add partner companies under Participants first.</div>}
      </Modal>
    </span>
  );
}

// ---- BAR (resolve page) ----------------------------------------------------
// `fields` picks which controls to show (default all three).
// ---- RESPONSIBLE PEOPLE, with an x on each ----------------------------------
// Everyone the issue is currently written to - responsible users, the
// assignee, and no-account contacts - as chips. Anyone who may edit the
// issue can take a person off it, and from then on that person gets
// none of its notifications or emails (DELETE /issue/:id/responsibles/:who).
// Removing the assignee clears the assignment as well.
export function IssueResponsibleChips({ issue, onChanged, size = "small" }) {
  const allowed = useIssueEditAllowed();
  const [busy, setBusy] = useState("");
  const rows = [];
  const seen = new Set();
  for (const r of issue?.responsibleUsers || []) {
    const u = r?.userId !== undefined ? r.userId : r;
    const id = idOf(u);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    rows.push({ key: id, who: id, label: (u && typeof u === "object") ? ([u.name, u.lastName].filter(Boolean).join(" ") || u.email || "Responsible") : "Responsible" });
  }
  const a = issue?.assignedTo?.userId;
  if (a && !seen.has(idOf(a))) {
    rows.push({ key: idOf(a), who: idOf(a), label: `${(typeof a === "object" ? ([a.name, a.lastName].filter(Boolean).join(" ") || a.email) : "Assignee")} (assignee)` });
  }
  for (const c of issue?.responsibleContacts || []) {
    if (!c?.email) continue;
    rows.push({ key: `contact:${c.email}`, who: `contact:${encodeURIComponent(c.email)}`, label: `${c.name || c.email} (link)` });
  }
  if (!rows.length) return null;
  const remove = async (row) => {
    setBusy(row.key);
    try {
      const resp = await fetch(`${url}/issue/${issue._id}/responsibles/${row.who}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` },
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) { message.error(body.err || "Could not remove"); return; }
      message.success(`${body.removed || row.label} removed - they will no longer be notified about this issue`);
      await onChanged?.({ responsibles: true, responsibleUsers: body.responsibleUsers, responsibleContacts: body.responsibleContacts, assignedTo: body.assignedTo });
    } catch (e) {
      message.error(e.message || "Could not remove");
    } finally {
      setBusy("");
    }
  };
  return (
    <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
      {rows.map((row) => (
        allowed ? (
          <Popconfirm
            key={row.key}
            title={`Remove ${row.label} from this issue?`}
            description="They will stop getting its notifications and emails."
            okText="Remove"
            okButtonProps={{ danger: true, loading: busy === row.key }}
            onConfirm={() => remove(row)}
          >
            <Tag closable onClose={(e) => e.preventDefault()} style={{ margin: 0, cursor: "pointer", fontSize: size === "small" ? 11 : 12 }} title="Click the x to take this person off the issue">
              {row.label}
            </Tag>
          </Popconfirm>
        ) : (
          <Tag key={row.key} style={{ margin: 0, fontSize: size === "small" ? 11 : 12 }}>{row.label}</Tag>
        )
      ))}
    </span>
  );
}

export function IssueActionBar({ issue, onChanged, fields = ["status", "priority", "due", "assign"] }) {
  const labelStyle = { fontSize: 10.5, fontWeight: 600, color: T.muted, letterSpacing: ".06em" };
  const divider = <span style={{ width: 1, height: 20, background: T.line }} />;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
      background: "#fff", border: `1px solid ${T.line}`, borderRadius: 6,
      padding: "6px 10px", marginBottom: 8, flexShrink: 0,
    }}>
      {fields.includes("status") && (<>
        <span style={labelStyle}>STATUS</span>
        <IssueStatusSelect issue={issue} onChanged={onChanged} />
        {divider}
      </>)}
      {fields.includes("priority") && (<>
        <span style={labelStyle}>PRIORITY</span>
        <IssuePrioritySelect issue={issue} onChanged={onChanged} />
        {divider}
      </>)}
      {fields.includes("due") && (<>
        <span style={labelStyle}>DUE</span>
        <IssueDueDateSelect issue={issue} onChanged={onChanged} />
        {divider}
      </>)}
      {fields.includes("assign") && (<>
        <span style={labelStyle}>ASSIGNED TO</span>
        <IssueAssignSelect issue={issue} onChanged={onChanged} />
      </>)}
      {fields.includes("partner") && <IssueAddPartner issue={issue} onChanged={onChanged} />}
      <span style={{ flex: 1 }} />
      <span style={{ fontSize: 10.5, color: T.sub }}>Resolved / abandoned go through the review flow</span>
    </div>
  );
}

export default IssueActionBar;
